import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ArrowRight, GitCompare } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const comparisons = [
  {
    title: 'Hazel Alternatives for Mac (2026)',
    href: '/compare/hazel-alternative-mac',
    summary: 'The pillar comparison covering Sortio, Hazel, Sparkle, FileJuggler, DropIt, and Folder Tidy. Side-by-side table, honest per-tool verdicts, plus where Hazel still wins and where the AI-native options pull ahead.',
    featured: true,
  },
  {
    title: 'Hazel vs Sortio',
    href: '/hazel-vs-sortio',
    summary: 'The direct head-to-head: deterministic Mac rules and a one-time license versus AI prompts that read document contents on Mac and Windows. Honest rows on pricing, platforms, and what each tool cannot do.',
  },
  {
    title: 'Hazel for Windows',
    href: '/hazel-for-windows',
    summary: 'Hazel is Mac-only. What Windows users should use instead, what carries over, and where the closest equivalents fall short.',
  },
  {
    title: 'Sparkle vs Sortio',
    href: '/compare/sparkle-vs-sortio',
    summary: 'The two Mac AI organizers head to head. Pricing, what each AI actually reads, rule customization, OS support, undo behavior, and which one fits which job.',
  },
  {
    title: 'FileJuggler vs Sortio',
    href: '/compare/filejuggler-vs-sortio',
    summary: 'FileJuggler is the long-running Windows rule engine with OCR. Sortio is the Mac-native AI alternative that also runs on Windows. Full migration notes for switchers.',
  },
  {
    title: 'Filect vs Sortio',
    href: '/compare/filect-vs-sortio',
    summary: 'Filect organizes in the background and adds natural-language search across your whole drive, cloud only, from $15/mo. Sortio gives you a plan to approve and an undo. Where each one fits.',
  },
  {
    title: 'FilesMagic AI vs Sortio',
    href: '/compare/filesmagic-ai-vs-sortio',
    summary: 'FilesMagic AI is Mac-only, sends filenames rather than contents when proposing a structure, and sells a lifetime license. Sortio reads document contents and runs on three platforms.',
  },
  {
    title: 'Zush vs Sortio',
    href: '/compare/zush-vs-sortio',
    summary: 'Zush renames files in place across 104 formats and never moves them. Sortio decides which folder a file belongs in. Why running both is a reasonable answer.',
  },
  {
    title: 'Sortio vs Hazel vs Sparkle (legacy)',
    href: '/guides/sortio-vs-sparkle-vs-hazel',
    summary: 'Older three-way overview covering rule-based vs AI-driven sorting, Mac-only vs cross-platform, learning curve, and pricing.',
  },
  {
    title: 'Sortio plus Hazel (workflow)',
    href: '/guides/sortio-plus-hazel-workflow',
    summary: 'Run Hazel and Sortio side by side. Hazel handles the deterministic stuff, Sortio handles the messy stuff. Setup walkthrough plus example rule that hands off PDFs to Sortio.',
  },
  {
    title: 'Migrate from Hazel to Sortio',
    href: '/guides/migrate-from-hazel-to-sortio',
    summary: 'Practical step-by-step guide. Maps Hazel concepts (rule, condition, action, attribute) to Sortio equivalents, with translations of common Hazel rule patterns.',
  },
  {
    title: 'Sortio plus Raycast',
    href: '/guides/sortio-plus-raycast',
    summary: 'Trigger Sortio sorts from a Raycast command. Useful if you live in Spotlight-style launchers and want zero-Finder file organization.',
  },
  {
    title: 'Sortio plus Apple Shortcuts',
    href: '/guides/sortio-plus-apple-shortcuts',
    summary: 'Drive Sortio from Apple Shortcuts so AI sorting can be the last step in any iOS or macOS automation pipeline.',
  },
  {
    title: 'Best AI File Organizer for Mac',
    href: '/guides/best-ai-file-organizer-mac',
    summary: 'Ranked review of the AI file organizers available for macOS in 2026: Sortio, Hazel, Sparkle, Maid, DropIt, plus the native Smart Folders.',
  },
];

const ComparePage = () => {
  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
        { '@type': 'ListItem', position: 2, name: 'Compare', item: 'https://www.getsortio.com/compare' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Sortio Comparisons',
      url: 'https://www.getsortio.com/compare',
      hasPart: comparisons.map((c) => ({
        '@type': 'Article',
        headline: c.title,
        url: `https://www.getsortio.com${c.href}`,
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-black text-apple-100 py-24">
      <SEOHead
        title="Compare Sortio vs Hazel, Sparkle, and Other File Organizers"
        description="How Sortio compares to Hazel, Sparkle, and the other Mac and Windows file organizers. Side-by-side guides, integration workflows, and a roundup of the best AI file organizers."
        canonical="https://www.getsortio.com/compare"
        schema={schema}
      />

      <div className="container mx-auto px-6 max-w-4xl relative z-10">
        <Link to="/" className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-8 transition-colors group">
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Home</span>
        </Link>

        <div className="mb-12 flex flex-col items-center">
          <div className="w-14 h-14 rounded-full bg-apple-700/60 backdrop-blur-sm flex items-center justify-center mb-6 border border-apple-600/30">
            <GitCompare className="w-6 h-6 text-sortio-primary" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-center">
            Compare <span className="apple-gradient-text">Sortio</span>
          </h1>
          <p className="text-apple-300 mt-4 text-center max-w-2xl">
            Side-by-side comparisons against Hazel, Sparkle, and other popular file organizers, plus workflows for combining Sortio with launchers like Raycast and Apple Shortcuts.
          </p>
        </div>

        <div className="grid gap-4">
          {comparisons.map((c) => (
            <Link key={c.href} to={c.href} className="apple-panel p-6 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-medium text-apple-50 mb-2 group-hover:text-sortio-primary transition-colors">{c.title}</h2>
                  <p className="text-apple-300 text-sm leading-relaxed">{c.summary}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ComparePage;
